import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, Mail, MapPin, Phone } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import AnimateIn from '../components/AnimateIn';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Contact = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', business: '', website: '', google_page: '',
    message: location.state?.prefill || '',
  });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [validationMsg, setValidationMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const stagger = { animate: { transition: { staggerChildren: 0.15 } } };
  const fadeInUp = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 } };

  const stepTitles = [t('cwStep1Title'), t('cwStep2Title'), t('cwStep3Title')];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setValidationMsg('');
  };

  const goNext = () => {
    if (step === 0) {
      if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
        setValidationMsg(t('cwFillRequired'));
        return;
      }
      if (!EMAIL_RE.test(formData.email.trim())) {
        setValidationMsg(t('cwInvalidEmail'));
        return;
      }
    }
    setDirection(1);
    setStep((s) => s + 1);
  };

  const goBack = () => {
    setDirection(-1);
    setValidationMsg('');
    setStep((s) => s - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.message.trim()) {
      setValidationMsg(t('cwFillRequired'));
      return;
    }
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Failed to send message');
      setStatus('success');
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again or email us directly.');
      setTimeout(() => setStatus('idle'), 6000);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', business: '', website: '', google_page: '', message: '' });
    setStep(0);
    setStatus('idle');
  };

  const slideVariants = {
    enter: (dir) => ({ opacity: 0, x: dir * 60 }),
    center: { opacity: 1, x: 0 },
    exit: (dir) => ({ opacity: 0, x: dir * -60 }),
  };

  const field = (name, labelKey, placeholder, { type = 'text', required = false, testId } = {}) => (
    <div>
      <label className="block text-sm font-medium text-gray-200 mb-2">
        {t(labelKey)} {required && <span className="text-red-400">*</span>}
      </label>
      <input
        type={type} name={name} value={formData[name]} onChange={handleChange}
        className="form-input" placeholder={placeholder}
        data-testid={testId}
      />
    </div>
  );

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16 px-6 lg:px-8 overflow-hidden">
        <div
          className="absolute -top-24 right-0 w-[480px] h-[480px] rounded-full blur-[150px] opacity-[0.12] pointer-events-none"
          style={{ background: 'linear-gradient(135deg, #706958, #e2c179)' }}
        />
        <div className="relative z-10 max-w-[1200px] mx-auto">
          <motion.div initial="initial" animate="animate" variants={stagger}>
            <motion.p className="accent-tag" variants={fadeInUp} transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}>
              {t('contactTag')}
            </motion.p>
            <motion.h1
              className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold leading-[1.1] tracking-[-0.02em] text-white max-w-3xl mb-8"
              style={{ fontFamily: "'Syne', sans-serif" }}
              variants={fadeInUp}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              data-testid="contact-title"
            >
              {t('contactTitle')}
            </motion.h1>
            <motion.p
              className="text-base md:text-lg leading-[1.8] max-w-2xl text-gray-400"
              variants={fadeInUp}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              data-testid="contact-subtitle"
            >
              {t('contactSubtitle')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Wizard + Info */}
      <section className="pb-20 md:pb-28 px-6 lg:px-8">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 lg:gap-24">
            {/* Wizard */}
            <AnimateIn className="lg:col-span-3" direction="left">
              {status === 'success' ? (
                <motion.div
                  className="rounded-2xl border border-white/10 p-10 md:p-14 text-center"
                  style={{ background: 'radial-gradient(ellipse at 50% -20%, rgba(74,222,128,0.08) 0%, rgba(10,10,10,0.6) 60%)' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  data-testid="form-success-message"
                >
                  <CheckCircle size={48} className="mx-auto mb-6" style={{ color: '#4ade80' }} />
                  <h3
                    className="text-2xl md:text-3xl font-bold text-white mb-4"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                  >
                    {t('cwSuccessTitle')}
                  </h3>
                  <p className="text-gray-400 text-sm md:text-base mb-8">{t('cwSuccessText')}</p>
                  <button className="btn-outline" onClick={resetForm} data-testid="contact-send-another-btn">
                    {t('cwSendAnother')}
                  </button>
                </motion.div>
              ) : (
                <div
                  className="rounded-2xl border border-white/10 p-7 md:p-10"
                  style={{ background: 'rgba(255,255,255,0.02)' }}
                >
                  {/* Step indicator */}
                  <div className="flex items-center gap-0 mb-10" data-testid="wizard-steps">
                    {stepTitles.map((title, i) => (
                      <div key={i} className={`flex items-center ${i < 2 ? 'flex-1' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div
                            className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-500 shrink-0"
                            style={{
                              fontFamily: "'Syne', sans-serif",
                              background: i <= step ? 'linear-gradient(135deg, #706958, #e2c179)' : 'rgba(255,255,255,0.06)',
                              color: i <= step ? '#0a0a0a' : '#6b7280',
                              border: i <= step ? 'none' : '1px solid rgba(255,255,255,0.1)',
                            }}
                            data-testid={`wizard-step-${i + 1}`}
                          >
                            {i < step ? <CheckCircle size={15} /> : `0${i + 1}`}
                          </div>
                          <span
                            className={`hidden sm:block text-xs font-semibold tracking-wide transition-colors duration-500 ${i === step ? 'text-white' : 'text-gray-600'}`}
                          >
                            {title}
                          </span>
                        </div>
                        {i < 2 && (
                          <div className="flex-1 h-px mx-4 relative overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                            <div
                              className="absolute inset-y-0 left-0 transition-all duration-700"
                              style={{ width: i < step ? '100%' : '0%', background: 'linear-gradient(90deg, #706958, #e2c179)' }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Error feedback */}
                  {status === 'error' && (
                    <div
                      className="flex items-center gap-3 p-4 mb-6 rounded-xl border"
                      style={{ background: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.3)', color: '#fca5a5' }}
                      data-testid="form-error-message"
                    >
                      <p className="text-sm font-medium">{errorMsg}</p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <div className="overflow-hidden">
                      <AnimatePresence mode="wait" custom={direction}>
                        <motion.div
                          key={step}
                          custom={direction}
                          variants={slideVariants}
                          initial="enter"
                          animate="center"
                          exit="exit"
                          transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                          className="space-y-5"
                        >
                          {step === 0 && (
                            <div className="space-y-5" data-testid="wizard-panel-1">
                              {field('name', 'contactNameLabel', 'John Doe', { required: true, testId: 'contact-name-input' })}
                              {field('email', 'contactEmailLabel', 'john@example.com', { type: 'email', required: true, testId: 'contact-email-input' })}
                              {field('phone', 'contactPhoneLabel', '+1 (514) 000-0000', { type: 'tel', required: true, testId: 'contact-phone-input' })}
                            </div>
                          )}
                          {step === 1 && (
                            <div className="space-y-5" data-testid="wizard-panel-2">
                              <p className="text-xs text-gray-500 -mb-1">{t('cwOptionalHint')}</p>
                              {field('business', 'contactBusinessLabel', 'Your Business Inc.', { testId: 'contact-business-input' })}
                              {field('website', 'contactWebsiteLabel', 'https://yourbusiness.com', { testId: 'contact-website-input' })}
                              {field('google_page', 'contactGoogleLabel', 'https://g.page/yourbusiness', { testId: 'contact-google-input' })}
                            </div>
                          )}
                          {step === 2 && (
                            <div data-testid="wizard-panel-3">
                              <label className="block text-sm font-medium text-gray-200 mb-2">
                                {t('contactMessageLabel')} <span className="text-red-400">*</span>
                              </label>
                              <textarea
                                name="message" value={formData.message} onChange={handleChange}
                                className="form-input min-h-[160px] resize-y"
                                placeholder="Tell us about your project or business goals..."
                                data-testid="contact-message-input"
                              />
                            </div>
                          )}
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    {validationMsg && (
                      <p className="mt-4 text-sm" style={{ color: '#fca5a5' }} data-testid="wizard-validation-error">
                        {validationMsg}
                      </p>
                    )}

                    {/* Nav buttons */}
                    <div className="flex items-center justify-between mt-8">
                      {step > 0 ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-white transition-colors"
                          onClick={goBack}
                          data-testid="wizard-back-btn"
                        >
                          <ArrowLeft size={15} />
                          {t('cwBack')}
                        </button>
                      ) : <span />}
                      {step < 2 ? (
                        <button type="button" className="btn-primary" onClick={goNext} data-testid="wizard-next-btn">
                          {t('cwNext')}
                          <ArrowRight size={16} />
                        </button>
                      ) : (
                        <button
                          type="submit"
                          className="btn-primary"
                          disabled={status === 'sending'}
                          data-testid="contact-submit-btn"
                        >
                          {status === 'sending' ? t('cwSending') : t('cwSendInquiry')}
                          {status !== 'sending' && <ArrowRight size={16} />}
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              )}
            </AnimateIn>

            {/* Info */}
            <AnimateIn className="lg:col-span-2" delay={0.2} direction="right">
              <h3
                className="text-xl font-semibold text-white mb-4"
                style={{ fontFamily: "'Syne', sans-serif" }}
                data-testid="contact-info-title"
              >
                {t('contactInfoTitle')}
              </h3>
              <p className="text-sm leading-relaxed mb-10 text-gray-400">{t('contactInfoText')}</p>

              <div className="space-y-6">
                <div className="flex items-start gap-4" data-testid="contact-email-section">
                  <Mail size={20} strokeWidth={1.5} style={{ color: '#e2c179' }} className="mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-white mb-0.5">Email</p>
                    <a
                      href="mailto:Casper@cexus.ca"
                      className="text-sm transition-colors hover:opacity-70"
                      style={{ color: '#e2c179' }}
                      data-testid="contact-email-link"
                    >
                      Casper@cexus.ca
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-phone-section">
                  <Phone size={20} strokeWidth={1.5} style={{ color: '#e2c179' }} className="mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-white mb-0.5">{t('contactPhoneTitle')}</p>
                    <a
                      href="tel:+14383760472"
                      className="text-sm transition-colors hover:opacity-70"
                      style={{ color: '#e2c179' }}
                      data-testid="contact-phone-link"
                    >
                      (438) 376-0472
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-location-section">
                  <MapPin size={20} strokeWidth={1.5} style={{ color: '#e2c179' }} className="mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-white mb-0.5">Location</p>
                    <p className="text-sm text-gray-400">Montreal, QC, Canada</p>
                  </div>
                </div>
              </div>
            </AnimateIn>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
